import { ButtonHTMLAttributes, forwardRef, ElementType } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  shape?: 'default' | 'icon';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconClassName?: string;
  isLoading?: boolean;
  fullWidth?: boolean;
  as?: ElementType;
  to?: string; // For React Router Link
  href?: string; // For standard anchor tags
  target?: string;
  rel?: string;
  title?: string;
}

/**
 * Standardized Polymorphic Button component for Mondivago.
 * Aligned with DESIGN.md and COMPONENTS.md.
 * Reference: Minimal dashboard primary actions.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      shape = 'default',
      icon: Icon,
      iconPosition = 'left',
      iconClassName = '',
      isLoading = false,
      fullWidth = false,
      as: Component = 'button',
      className = '',
      disabled,
      type = Component === 'button' ? 'button' : undefined,
      title,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-2 font-bold transition-all duration-300 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

    const variants = {
      primary: 'bg-lime-800 text-lime-100 hover:bg-lime-900',
      secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      tertiary: 'bg-transparent text-gray-600 hover:text-gray-900 px-0 py-0',
      ghost: 'bg-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-800',
      danger: 'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/10',
      outline: 'bg-white border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900',
    };

    const sizes = {
      sm: 'px-4 py-2 text-xs rounded-full',
      md: 'px-5 py-3 text-sm rounded-full',
      lg: 'px-8 py-4 text-base rounded-[20px]',
    };

    const widthStyle = fullWidth ? 'w-full' : 'w-auto';

    // Icon-only shape: square, centered, no padding, fixed dimensions
    const isIconShape = shape === 'icon';
    const iconShapeSizes = {
      sm: 'h-8 w-8',
      md: 'h-10 w-10',
      lg: 'h-12 w-12',
    };
    const iconShapeClass = isIconShape
      ? `!rounded-full !p-0 aspect-square shrink-0 flex items-center justify-center ${iconShapeSizes[size]
          .split(' ')
          .map((s) => `!${s}`)
          .join(' ')}`
      : '';

    const iconSizes = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };
    const iconClass = isIconShape
      ? `${iconSizes[size]} ${iconClassName}`
      : `${size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} ${iconClassName}`;

    // Tertiary only text variant shouldn't have padding/bg
    const isTertiary = variant === 'tertiary';
    const finalSizeClass = isTertiary ? 'text-sm' : sizes[size];

    return (
      <Component
        ref={ref}
        disabled={disabled ?? isLoading}
        type={type}
        title={title}
        className={`${baseStyles} ${variants[variant]} ${finalSizeClass} ${widthStyle} ${iconShapeClass} ${className}`}
        {...props}
      >
        {isLoading ? (
          isIconShape ? (
            <span className={`${iconSizes[size]} animate-spin rounded-full border-2 border-current border-t-transparent`} />
          ) : (
            <span>Please wait...</span>
          )
        ) : (
          <>
            {Icon && iconPosition === 'left' && <Icon className={iconClass} />}
            {children || title}
            {Icon && iconPosition === 'right' && <Icon className={iconClass} />}
          </>
        )}
      </Component>
    );
  }
);

Button.displayName = 'Button';
